import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { usePuidsSelector } from 'shared/hooks/usePuidsSelector';
import { selectRegionId } from 'shared/selectors/location';
import { v4 as uuidv4 } from 'uuid';

import { TAdBannerCommonParameters } from '../types';

/**
 * Общие, региональные параметры баннера: готовые puids, regionId и стабильный `placeId`.
 *
 * `placeId` генерируется один раз на смонтированный баннер и должен быть уникален между
 * независимыми React-корнями на странице, поэтому используется UUID v4 (первые 8 символов),
 * а не React `useId()`. Значение DOM/CSS-безопасно — ad-banner-frontend строит из него id
 * Adfox-контейнера.
 */
export function useAdBannerCommonParameters(): TAdBannerCommonParameters {
  const rawPuids = usePuidsSelector();
  const regionId = useSelector(selectRegionId);
  const placeId = useMemo(() => uuidv4().slice(0, 8), []);

  const puids = useMemo<Record<string, string>>(
    () =>
      Object.entries(rawPuids).reduce<Record<string, string>>((acc, [key, value]) => {
        if (value !== null) {
          acc[key] = value;
        }

        return acc;
      }, {}),
    [rawPuids],
  );

  return useMemo(() => ({ puids, regionId, placeId }), [placeId, puids, regionId]);
}
